import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  CreditCard,
  Building2,
  QrCode,
  Save,
  Loader,
  RefreshCw,
} from "lucide-react";
import {
  lmsGetPaymentConfigBySchoolId,
  lmsSavePaymentConfig,
} from "../api/lmsApi";
import "../lms.css";

const getSchoolData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const adminId = ud.admin_id || ud.school_id || ud.id || null;
    return { admin_id: adminId, ...ud };
  } catch {
    return { admin_id: null };
  }
};

export default function PaymentConfig() {
  const school = getSchoolData();
  const schoolId = school.admin_id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configId, setConfigId] = useState(null);

  const [formData, setFormData] = useState({
    upi_id: "",
    upi_account_name: "",
    bank_name: "",
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    branch_name: "",
    status: 1,
  });

  useEffect(() => {
    if (schoolId) {
      fetchPaymentConfig();
    }
  }, [schoolId]);

  const fetchPaymentConfig = async () => {
    setLoading(true);
    try {
      const res = await lmsGetPaymentConfigBySchoolId(schoolId);
      const data = res.data?.data || res.data;
      if (data && data.id) {
        setConfigId(data.id);
        setFormData({
          upi_id: data.upi_id || "",
          upi_account_name: data.upi_account_name || "",
          bank_name: data.bank_name || "",
          account_holder_name: data.account_holder_name || "",
          account_number: data.account_number || "",
          ifsc_code: data.ifsc_code || "",
          branch_name: data.branch_name || "",
          status: data.status !== undefined ? Number(data.status) : 1,
        });
      } else {
        setConfigId(null);
      }
    } catch (error) {
      console.error("Error fetching payment configuration:", error);
      toast.error("Failed to load payment configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!schoolId) {
      toast.error("School ID not found");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        school_id: schoolId,
        ...formData,
        status: Number(formData.status),
      };

      const res = await lmsSavePaymentConfig(payload);
      const savedData = res.data?.data;
      if (savedData && savedData.id) {
        setConfigId(savedData.id);
      }
      toast.success(res.data?.message || "Payment configuration saved successfully!");
    } catch (error) {
      console.error("Error saving payment configuration:", error);
      toast.error(error.response?.data?.message || "Failed to save payment configuration");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Page Header */}
      <div className="std-page-header" style={{ marginBottom: "24px" }}>
        <div>
          <h1 className="std-page-title" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <CreditCard className="text-blue-600" size={26} />
            Payment Configuration
          </h1>
          <p className="std-page-subtitle">
            Configure UPI ID and Bank Account details for course fee transactions (School ID: {schoolId || "N/A"})
          </p>
        </div>
        <button
          type="button"
          className="std-btn-cancel"
          onClick={fetchPaymentConfig}
          disabled={loading}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          <RefreshCw size={16} className={loading ? "spinner" : ""} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
          <Loader className="spinner" size={32} style={{ margin: "0 auto 12px", color: "#1c498d" }} />
          <div style={{ color: "#64748b", fontSize: "14px", fontWeight: "500" }}>Loading payment configuration...</div>
        </div>
      ) : (
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <form onSubmit={handleSubmit} style={{ background: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
              <Building2 size={18} color="#1c498d" />
              Configure Gateway Details
            </h3>

            {/* UPI Settings Section */}
            <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#1c498d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                <QrCode size={16} /> UPI & QR Settings
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div>
                  <label className="std-field-label">
                    UPI ID / VPA Handles
                  </label>
                  <input
                    type="text"
                    name="upi_id"
                    className="std-field-input"
                    placeholder="e.g. schoolname@icici or 9876543210@upi"
                    value={formData.upi_id}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label className="std-field-label">
                    UPI Account / Payee Name
                  </label>
                  <input
                    type="text"
                    name="upi_account_name"
                    className="std-field-input"
                    placeholder="e.g. Model Public School LMS"
                    value={formData.upi_account_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Bank Details Section */}
            <div style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#1c498d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                <Building2 size={16} /> Bank Account Details
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div style={{ gridColumn: "span 2" }}>
                  <label className="std-field-label">Bank Name</label>
                  <input
                    type="text"
                    name="bank_name"
                    className="std-field-input"
                    placeholder="e.g. HDFC Bank / ICICI Bank"
                    value={formData.bank_name}
                    onChange={handleInputChange}
                  />
                </div>

                <div style={{ gridColumn: "span 2" }}>
                  <label className="std-field-label">Account Holder Name</label>
                  <input
                    type="text"
                    name="account_holder_name"
                    className="std-field-input"
                    placeholder="e.g. School Educational Trust"
                    value={formData.account_holder_name}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label className="std-field-label">Account Number</label>
                  <input
                    type="text"
                    name="account_number"
                    className="std-field-input"
                    placeholder="e.g. 50100234567890"
                    value={formData.account_number}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <label className="std-field-label">IFSC Code</label>
                  <input
                    type="text"
                    name="ifsc_code"
                    className="std-field-input"
                    placeholder="e.g. HDFC0001234"
                    value={formData.ifsc_code}
                    onChange={handleInputChange}
                    style={{ textTransform: "uppercase" }}
                  />
                </div>

                <div style={{ gridColumn: "span 2" }}>
                  <label className="std-field-label">Branch Name</label>
                  <input
                    type="text"
                    name="branch_name"
                    className="std-field-input"
                    placeholder="e.g. Main Branch, MG Road"
                    value={formData.branch_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Status Select */}
            <div style={{ marginBottom: "24px" }}>
              <label className="std-field-label">Configuration Status</label>
              <select
                name="status"
                className="std-field-input"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value={1}>Active (Enabled for Fee Payments)</option>
                <option value={0}>Inactive (Disabled)</option>
              </select>
            </div>

            {/* Save Button */}
            <button
              type="submit"
              className="std-btn-primary"
              disabled={saving}
              style={{ width: "100%", justifyContent: "center", padding: "10px", fontSize: "14px" }}
            >
              {saving ? (
                <>
                  <Loader size={18} className="spinner" />
                  Saving Configuration...
                </>
              ) : (
                <>
                  <Save size={18} />
                  {configId ? "Update Payment Configuration" : "Save Payment Configuration"}
                </>
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
